import React, { useEffect, useState } from 'react';
// import DoneIcon from '@material-ui/icons/Done';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import ArchiveIcon from '@material-ui/icons/Archive';
import { makeStyles } from '@material-ui/core/styles';
import {
	CssBaseline,
	Container,
	Paper,
	Typography,
	Button,
	TextField,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	IconButton,
	Grid,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import {
	useGetActorsQuery,
	useGetAuthorsQuery,
	useGetCategoriesQuery,
	useAddCategoryMutation,
	useAddAuthorMutation,
	useAddActorMutation,
	useGetBookDetailsQuery,
	useUpdateAdminBookMutation,
	useDeleteBookAttachmentMutation,
	useCreateBookArchiveMutation,
	useDeleteBookArchiveMutation,
} from '../../../store/apiSlice';
import VirtualizeControlled from '../../../components/Virtualize';
import { useForm, Controller } from 'react-hook-form';
// popup
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Const from '../../../defs/const';

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		alignItems: 'center',
	},
	mt2: {
		marginTop: theme.spacing(2),
	},
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	paper: {
		padding: theme.spacing(2),
	},
	title: {
		paddingBottom: theme.spacing(2),
	},
	backButton: {
		marginBottom: theme.spacing(2),
	},
	flex: {
		display: 'flex',
		gap: 24,
		alignItems: 'center',
		justifyContent: 'center',
	},
	archiveActions: {
		display: 'flex',
		gap: theme.spacing(2),
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
	archiveStatus: {
		marginTop: theme.spacing(1),
	},
	pendingDelete: {
		color: theme.palette.error.light,
		textDecoration: 'line-through',
		opacity: 0.7,
	},
	progressBar: {
		width: '33%',
		backgroundColor: theme.palette.action.disabledBackground,
		height: 20,
		borderRadius: 3,
		overflow: 'hidden',
		position: 'relative',
	},
	progressFill: {
		position: 'absolute',
		top: 0,
		left: 0,
		height: '100%',
		backgroundColor: theme.palette.success.main,
	},
	progressText: {
		position: 'relative',
		zIndex: 1,
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(1),
		lineHeight: '20px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	checkIcon: {
		color: theme.palette.success.main,
		marginLeft: theme.spacing(1),
		verticalAlign: 'middle',
	},
}));

const ARCHIVE_BUSY_STATUSES = [
	Const.BOOK_ARCHIVE_STATUS_DOWNLOADING_FILES,
	Const.BOOK_ARCHIVE_STATUS_CREATING_ARCHIVE,
	Const.BOOK_ARCHIVE_STATUS_UPLOADING_ARCHIVE,
	Const.BOOK_ARCHIVE_STATUS_DELETING,
];

export function CatalogueEdit(props) {
	const classes = useStyles();
	const navigate = useNavigate();
	const { id } = useParams();

	const [pollingInterval, setPollingInterval] = useState(0);
	const { data } = useGetBookDetailsQuery(id, {
		pollingInterval,
	});
	const [filesData, setFilesData] = useState(data?.files);
	const archive = data?.archive || null;

	useEffect(() => {
		const files = data?.files || [];
		const hasPendingFileUploads = files.some((file) => {
			const progress = file?.ODBookFiles?.[0]?.progress;
			return typeof progress !== 'number' || progress < 100;
		});
		const shouldPollArchive = ARCHIVE_BUSY_STATUSES.includes(data?.archive?.status);
		const shouldPollOdArchive = ARCHIVE_BUSY_STATUSES.includes(
			data?.archive?.odArchive?.status
		);

		setPollingInterval(
			hasPendingFileUploads || shouldPollArchive || shouldPollOdArchive
				? 5000
				: 0
		);

		setFilesData(files);
	}, [data]);

	const [name, setName] = useState(''); // name for newly added entry (author, category, actor)

	const [addActor] = useAddActorMutation();
	const [addCategory] = useAddCategoryMutation();
	const [addAuthor] = useAddAuthorMutation();

	// RTK Query mutations
	const [updateBook] = useUpdateAdminBookMutation();
	const [deleteBookAttachment] = useDeleteBookAttachmentMutation();
	const [createBookArchive, { isLoading: isCreatingArchive }] =
		useCreateBookArchiveMutation();
	const [deleteBookArchive, { isLoading: isDeletingArchive }] =
		useDeleteBookArchiveMutation();

	// popup state
	const [open, setOpen] = useState(false);
	const [popUpType, setPopUpType] = useState('');
	const [fileUploadError, setFileUploadError] = useState('');
	const [submitError, setSubmitError] = useState('');
	const [archiveActionError, setArchiveActionError] = useState('');

	const handleCreateEntry = () => {
		if (popUpType === 'actor') {
			addActor({ name });
		} else if (popUpType === 'author') {
			addAuthor({ name });
		} else if (popUpType === 'category') {
			addCategory({ name });
		} else {
			console.error('No such entry type.');
		}
		setOpen(false);
	};

	const handleClose = () => {
		setName('');
		setOpen(false);
	};

	const { data: authors = [] } = useGetAuthorsQuery();
	const { data: actors = [] } = useGetActorsQuery();
	const { data: categories = [] } = useGetCategoriesQuery();

	const {
		handleSubmit,
		control,
		setValue,
		reset,
		trigger,
		formState: { errors },
	} = useForm({
		defaultValues: {
			title: '',
			author: '',
			category: '',
			year: '',
			actor: '',
			identifier: '',
			len: '',
			newFiles: null, // file input field
			files: '',
		},
	});

	useEffect(() => {
		const book = data?.book;
		if (book) {
			reset({
				title: book.title,
				author: book.author?.id,
				category: book.category?.id,
				year: book.year,
				actor: book.actor?.id,
				identifier: book?.code,
				len: book.duration,
			});
		}
	}, [data, reset]);

	const onSubmit = async (values) => {
		console.log('Form Submitted. VALUES:', values);
		setSubmitError('');

		if (values.newFiles && values.newFiles.length > Const.MAX_BOOK_UPLOAD_FILES) {
			const errorMessage = `Може да качите най-много ${Const.MAX_BOOK_UPLOAD_FILES} файла наведнъж.`;
			setFileUploadError(errorMessage);
			setSubmitError(errorMessage);
			return;
		}
		setFileUploadError('');

		// Create FormData to send as multipart/form-data
		const formData = new FormData();
		// Include the edited book's id
		formData.append('id', data.book.id);
		formData.append('title', values.title);
		formData.append('author', values.author);
		formData.append('category', values.category);
		formData.append('year', values.year);
		formData.append('actor', values.actor);
		formData.append('identifier', values.identifier);
		formData.append('len', values.len);

		// Append each file from newFiles (if any)
		if (values.newFiles && values.newFiles.length > 0) {
			for (let i = 0; i < values.newFiles.length; i++) {
				formData.append('newFiles', values.newFiles[i]);
			}
		}

		try {
			// Use RTK Query mutation to update the book.
			const result = await updateBook(formData).unwrap();
			console.log('Updated book:', result);
			navigate(`/catalogue/${data.book.id}`);
		} catch (error) {
			console.error('Error updating book:', error);
			setSubmitError(
				error?.data?.message ||
					error?.data?.error ||
					'Грешка при качването на файловете или корекцията на книгата.'
			);
		}
	};

	const handleDeleteAttachment = (fileId) => {
		deleteBookAttachment(fileId)
			.unwrap()
			.then(() => {
				setFilesData((prevState) =>
					(prevState || []).filter((f) => f.id !== fileId)
				);
			})
			.catch((err) => console.error('Error deleting attachment:', err));
	};

	const effectiveFiles = filesData || [];
	const hasFiles = effectiveFiles.length > 0;
	const hasIncompleteFileUploads = effectiveFiles.some((file) => {
		const progress = file?.ODBookFiles?.[0]?.progress;
		return typeof progress !== 'number' || progress < 100;
	});
	const archiveExists = Boolean(archive?.exists);
	const hasArchiveRecord = Boolean(archive?.id);
	const isArchiveBusy =
		ARCHIVE_BUSY_STATUSES.includes(archive?.status) ||
		ARCHIVE_BUSY_STATUSES.includes(archive?.odArchive?.status);
	const archiveMutationInFlight = isCreatingArchive || isDeletingArchive;
	const disableAddArchive =
		!hasFiles ||
		hasIncompleteFileUploads ||
		archiveExists ||
		isArchiveBusy ||
		archiveMutationInFlight;
	const disableDeleteArchive =
		!hasArchiveRecord || isArchiveBusy || archiveMutationInFlight;

	const archiveStatusLabel = (() => {
		if (!archive) return 'Архивът не е създаден.';
		if (archiveExists) return 'Архивът е наличен.';
		if (
			isArchiveBusy &&
			typeof archive?.odArchive?.progress === 'number' &&
			archive.odArchive.progress > 0
		) {
			return `Статус на архива: ${archive.status || archive.odArchive?.status} (${archive.odArchive.progress}%).`;
		}
		if (archive?.status === Const.BOOK_ARCHIVE_STATUS_FAILED)
			return 'Създаването или изтриването на архива е завършило с грешка.';
		if (archive?.status) return `Статус на архива: ${archive.status}.`;
		return 'Архивът не е създаден.';
	})();

	const handleCreateArchive = async () => {
		setArchiveActionError('');
		try {
			await createBookArchive(id).unwrap();
		} catch (error) {
			console.error('Error creating archive:', error);
			setArchiveActionError(
				error?.data?.message || 'Грешка при създаването на архива.'
			);
		}
	};

	const handleDeleteArchive = async () => {
		setArchiveActionError('');
		try {
			await deleteBookArchive(id).unwrap();
		} catch (error) {
			console.error('Error deleting archive:', error);
			setArchiveActionError(
				error?.data?.message || 'Грешка при изтриването на архива.'
			);
		}
	};

	return (
		<div className={classes.root}>
			<CssBaseline />
			<Helmet>
				<title>Корекция на книга</title>
			</Helmet>

			<Container maxWidth="lg" className={classes.container}>
				<Paper className={classes.paper}>
					<Typography
						className={classes.title}
						component="h1"
						variant="h4"
						role="alert"
					>
						Корекция на книга
					</Typography>

					<form onSubmit={handleSubmit(onSubmit)}>
						<Grid
							container
							spacing={1}
							justifyContent="flex-start"
							alignItems="flex-start"
							direction="row"
						>
							<Grid item xs={12}>
								<Controller
									name="title"
									control={control}
									rules={{
										required: { value: true, message: 'Задължително поле' },
									}}
									render={({ field }) => (
										<TextField
											label="Заглавие"
											fullWidth
											error={!!errors.title}
											helperText={errors?.title?.message}
											id="title"
											{...field}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<VirtualizeControlled
									label="Автор"
									id="author_id"
									options={[{ name: '------------' }, ...authors]}
									handleChange={(e, options) => {
										setValue('author', options?.id);
									}}
									control={control}
									error={!!errors.author}
									helperText={errors?.author?.message}
									rules={{
										required: { value: true, message: 'Задължително поле' },
									}}
									name="author"
									defaultValue={{
										name: data?.book.author?.name || 'Няма',
										id: parseInt(data?.book.author?.id, 10),
									}}
								/>
								<Button
									startIcon={<AddIcon />}
									onClick={() => {
										setOpen(true);
										setPopUpType('author');
									}}
								>
									Добави автор
								</Button>
							</Grid>
							<Grid item xs={12}>
								<VirtualizeControlled
									label="Категория"
									id="category"
									options={[{ name: '------------' }, ...categories]}
									handleChange={(e, options) =>
										setValue('category', options?.id)
									}
									control={control}
									error={!!errors.category}
									helperText={errors?.category?.message}
									rules={{
										required: { value: true, message: 'Задължително поле' },
									}}
									name="category"
									defaultValue={{
										name: data?.book.category?.name || 'Няма',
										id: parseInt(data?.book.category?.id, 10),
									}}
								/>
								<Button
									startIcon={<AddIcon />}
									onClick={() => {
										setOpen(true);
										setPopUpType('category');
									}}
								>
									Добави категория
								</Button>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="year"
									control={control}
									rules={{
										required: { value: true, message: 'Задължително поле' },
										onChange: () => {
											trigger('year');
										},
									}}
									render={({ field }) => (
										<TextField
											label="Година"
											fullWidth
											type="number"
											error={!!errors.year}
											helperText={errors?.year?.message}
											id="year"
											{...field}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<VirtualizeControlled
									label="Актьор"
									id="actor"
									options={[{ name: '------------' }, ...actors]}
									handleChange={(e, options) => setValue('actor', options?.id)}
									control={control}
									error={!!errors.actor}
									helperText={errors?.actor?.message}
									rules={{
										required: { value: true, message: 'Задължително поле' },
									}}
									name="actor"
									defaultValue={{
										name: data?.book.actor?.name || 'Няма',
										id: parseInt(data?.book.actor?.id, 10),
									}}
								/>
								<Button
									startIcon={<AddIcon />}
									onClick={() => {
										setOpen(true);
										setPopUpType('actor');
									}}
								>
									Добави актьор
								</Button>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="identifier"
									control={control}
									rules={{
										required: { value: true, message: 'Задължително поле' },
									}}
									render={({ field }) => (
										<TextField
											label="Идентификатор"
											fullWidth
											error={!!errors.identifier}
											helperText={errors?.identifier?.message}
											id="identifier"
											{...field}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="len"
									control={control}
									rules={{
										required: { value: true, message: 'Задължително поле' },
									}}
									render={({ field }) => (
										<TextField
											label="Продължителност"
											fullWidth
											error={!!errors.len}
											helperText={errors?.len?.message}
											id="len"
											{...field}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<div className={classes.flex}>
									<Typography>Файлове</Typography>
								</div>
							</Grid>
						</Grid>
						<List>
							{effectiveFiles.map((file) => {
								const progress = file?.ODBookFiles?.[0]?.progress;
								const isPendingDelete =
									typeof progress === 'number' && progress < 0;
								return (
								<ListItem key={file.id}>
									<ListItemText
										primary={
											<>
												{typeof progress === 'number' &&
												progress >= 0 &&
												progress < 100 ? (
													<div
														className={classes.progressBar}
														aria-label={`Прогрес ${progress}%`}
													>
														<div
															className={classes.progressFill}
															style={{ width: `${progress}%` }}
														/>
														<div
															className={`${classes.progressText} ${
																isPendingDelete ? classes.pendingDelete : ''
															}`}
														>
															{file.name}
														</div>
													</div>
												) : (
													<div className={isPendingDelete ? classes.pendingDelete : undefined}>
														{file.name}
														{typeof progress === 'number' && progress === 100 ? (
															<CheckCircleOutlineIcon className={classes.checkIcon} fontSize="small" />
														) : null}
													</div>
												)}
											</>
										}
									/>
									<ListItemSecondaryAction>
										<IconButton
											onClick={() => handleDeleteAttachment(file.id)}
											edge="end"
											aria-label="изтрий"
											disabled={isPendingDelete}
										>
											<DeleteIcon />
										</IconButton>
									</ListItemSecondaryAction>
								</ListItem>
							);
							})}
						</List>
						<div className={classes.archiveActions}>
							<Button
								startIcon={<ArchiveIcon />}
								onClick={handleCreateArchive}
								disabled={disableAddArchive}
							>
								Add archive
							</Button>
							<Button
								startIcon={<DeleteIcon />}
								onClick={handleDeleteArchive}
								disabled={disableDeleteArchive}
							>
								Del archive
							</Button>
						</div>
						<Typography variant="body2" color="textSecondary" className={classes.archiveStatus}>
							{archiveStatusLabel}
						</Typography>
						{archiveActionError ? (
							<Typography variant="body2" color="error">
								{archiveActionError}
							</Typography>
						) : null}
						{/* New file input field */}
						<Grid item xs={12} className={classes.mt2}>
							<Controller
								name="newFiles"
								control={control}
								render={({ field }) => (
									<input
										type="file"
										multiple
										onChange={(e) => {
											const files = e.target.files;
											if (
												files &&
												files.length > Const.MAX_BOOK_UPLOAD_FILES
											) {
												setFileUploadError(
													`Може да качите най-много ${Const.MAX_BOOK_UPLOAD_FILES} файла наведнъж.`
												);
											} else {
												setFileUploadError('');
											}
											field.onChange(files);
										}}
									/>
								)}
							/>
							<Typography variant="body2" color="textSecondary">
								Максимум {Const.MAX_BOOK_UPLOAD_FILES} файла наведнъж.
							</Typography>
							{fileUploadError ? (
								<Typography variant="body2" color="error">
									{fileUploadError}
								</Typography>
							) : null}
						</Grid>
						<br />
						{submitError ? (
							<Typography variant="body2" color="error" className={classes.mt2}>
								{submitError}
							</Typography>
						) : null}
						<Button
							type="submit"
							startIcon={<CheckCircleOutlineIcon />}
							className={classes.mt2}
						>
							Запис
						</Button>
					</form>
				</Paper>
			</Container>
			<div>
				<Dialog
					open={open}
					onClose={handleClose}
					aria-labelledby="form-dialog-title"
				>
					{popUpType === 'author' ? (
						<DialogTitle id="form-dialog-title">Добави автор</DialogTitle>
					) : popUpType === 'category' ? (
						<DialogTitle id="form-dialog-title">Добави категория</DialogTitle>
					) : popUpType === 'actor' ? (
						<DialogTitle id="form-dialog-title">Добави актьор</DialogTitle>
					) : (
						<DialogTitle id="form-dialog-title">Invalid</DialogTitle>
					)}

					<DialogContent>
						<TextField
							autoFocus
							margin="dense"
							id={popUpType}
							label="Име"
							type="text"
							fullWidth
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={handleCreateEntry}
							startIcon={<AddIcon />}
							color="primary"
						>
							Запис
						</Button>
						<Button onClick={handleClose} color="primary">
							Отказ
						</Button>
					</DialogActions>
				</Dialog>
			</div>
		</div>
	);
}
